import React, { useEffect, useMemo, useRef, useState } from 'react';
import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';
import { copyIcon } from '../../../constant/icon';
import {
    copyToClipboard,
    shortenWalletAddress,
} from '../../../utils/commonFunction';
import AmountDisplay from '../../../components/amount/AmountDisplay';

/* ------------------------------------------------------------------ */
/* ExpandablePanel: smooth height + mask reveal                       */
/* ------------------------------------------------------------------ */

const ExpandablePanel = ({ isOpen, children, className = '' }) => {
    const innerRef = useRef(null);
    const [contentHeight, setContentHeight] = useState(0);

    useEffect(() => {
        const node = innerRef.current;
        if (!node) return undefined;
        const update = () => setContentHeight(node.scrollHeight);
        update();
        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(update);
            observer.observe(node);
            return () => observer.disconnect();
        }
        return undefined;
    }, []);

    const ease = 'cubic-bezier(0.22, 1, 0.36, 1)';
    const innerStyle = {
        WebkitMaskImage: 'linear-gradient(to bottom, black, black)',
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskSize: isOpen ? '100% 100%' : '100% 0%',
        maskImage: 'linear-gradient(to bottom, black, black)',
        maskRepeat: 'no-repeat',
        maskSize: isOpen ? '100% 100%' : '100% 0%',
        transform: isOpen ? 'translateY(0)' : 'translateY(-6px)',
        opacity: isOpen ? 1 : 0,
        transition: isOpen
            ? `mask-size 500ms ${ease} 120ms, -webkit-mask-size 500ms ${ease} 120ms, transform 500ms ${ease} 120ms, opacity 300ms ease-out 120ms`
            : `mask-size 250ms ${ease}, -webkit-mask-size 250ms ${ease}, transform 250ms ${ease}, opacity 200ms ease-in`,
    };

    return (
        <div
            style={{ height: isOpen ? `${contentHeight}px` : '0px' }}
            className={`overflow-hidden transition-[height] duration-[480ms] ease-[cubic-bezier(0.22,1,0.36,1)] will-change-[height] ${className}`}
        >
            <div ref={innerRef} style={innerStyle}>
                {children}
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* Helpers + small UI atoms                                           */
/* ------------------------------------------------------------------ */

const directionMetaMap = {
    send: {
        label: 'Sent',
        icon: 'fa-arrow-up',
        color: 'text-emerald-800 dark:text-emerald-400',
        amount: 'text-zinc-900 dark:text-zinc-100',
    },
    receive: {
        label: 'Received',
        icon: 'fa-arrow-down',
        color: 'text-teal-800 dark:text-teal-400',
        amount: 'text-teal-900 dark:text-teal-300',
    },
    claim: {
        label: 'Claimable',
        icon: 'fa-hand-holding-dollar',
        color: 'text-amber-800 dark:text-amber-300',
        amount: 'text-amber-900 dark:text-amber-300',
    },
};

// Deterministic SVG identicon (jazzicon) for a given wallet address.
const Avatar = ({ address, size = 36 }) => (
    <div
        className="inline-flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-zinc-100 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-700"
        style={{ width: size, height: size }}
    >
        {address ? (
            <Jazzicon diameter={size} seed={jsNumberForAddress(address)} />
        ) : (
            <i className="fa-solid fa-wallet text-[10px] text-zinc-400 dark:text-zinc-500" />
        )}
    </div>
);

const TOKEN_BADGE_PALETTE = {
    ETH: 'text-violet-900 bg-violet-100 ring-violet-200 dark:text-violet-300 dark:bg-violet-950/50 dark:ring-violet-800',
    WETH: 'text-violet-900 bg-violet-100 ring-violet-200 dark:text-violet-300 dark:bg-violet-950/50 dark:ring-violet-800',
    USDC: 'text-teal-900 bg-teal-50 ring-teal-200 dark:text-teal-300 dark:bg-teal-950/40 dark:ring-teal-800',
    USDT: 'text-emerald-900 bg-emerald-50 ring-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/40 dark:ring-emerald-800',
    DAI: 'text-amber-900 bg-amber-50 ring-amber-200 dark:text-amber-300 dark:bg-amber-950/40 dark:ring-amber-800',
    BTC: 'text-orange-900 bg-orange-50 ring-orange-200 dark:text-orange-300 dark:bg-orange-950/40 dark:ring-orange-800',
    WBTC: 'text-orange-900 bg-orange-50 ring-orange-200 dark:text-orange-300 dark:bg-orange-950/40 dark:ring-orange-800',
    MATIC: 'text-fuchsia-900 bg-fuchsia-50 ring-fuchsia-200 dark:text-fuchsia-300 dark:bg-fuchsia-950/40 dark:ring-fuchsia-800',
};

const tokenBadgeFor = (sym) =>
    TOKEN_BADGE_PALETTE[(sym || '').toUpperCase()] ||
    'text-zinc-800 bg-zinc-100 ring-zinc-200 dark:text-zinc-200 dark:bg-zinc-800/60 dark:ring-zinc-600';

const TokenChip = ({ name, image }) => {
    const sym = (name || '').toUpperCase();
    const cls = tokenBadgeFor(sym);
    const letter = sym.charAt(0) || '?';
    return (
        <span className="inline-flex items-center gap-1.5 min-w-0">
            {image ? (
                <img
                    src={image}
                    alt=""
                    className="h-5 w-5 flex-shrink-0 rounded-full ring-1 ring-zinc-200 dark:ring-zinc-700"
                />
            ) : (
                <span
                    className={`h-5 w-5 rounded-md ring-1 inline-flex items-center justify-center text-[9px] font-bold ${cls}`}
                >
                    {letter}
                </span>
            )}
            <span className="truncate text-[13px] font-medium text-zinc-800 dark:text-zinc-200">
                {name || '—'}
            </span>
        </span>
    );
};

const TypePill = ({ type = 'Direct', mode = 'send' }) => {
    const lower = (type || '').toLowerCase();
    const arrowIcon =
        mode === 'receive' ? 'fa-arrow-down' : mode === 'claim' ? 'fa-hand-holding-dollar' : 'fa-arrow-up';
    const arrowColor =
        mode === 'receive'
            ? 'text-teal-700 dark:text-teal-400'
            : mode === 'claim'
                ? 'text-amber-700 dark:text-amber-400'
                : 'text-emerald-800 dark:text-emerald-400';
    const classes =
        lower === 'milestone'
            ? 'border-violet-200 bg-violet-50 text-violet-900 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-200'
            : lower.includes('request')
                ? 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200'
                : lower === 'tracked'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
                    : 'border-zinc-200 bg-zinc-50 text-zinc-800 dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-zinc-200';
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${classes}`}
        >
            <i className={`fa-solid ${arrowIcon} text-[9px] ${arrowColor}`} />
            {type}
        </span>
    );
};

const StatusPill = ({ isClaimed, row }) => {
    const type = String(row?.type || '').toLowerCase();
    if (type.includes('request')) {
        if (row?.isAccepted || isClaimed) {
            return (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-teal-900 dark:border-teal-900/50 dark:bg-teal-950/40 dark:text-teal-200">
                    <span className="h-1.5 w-1.5 rounded-full bg-teal-600 dark:bg-teal-500" />
                    Accepted
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-sky-900 dark:border-sky-900/50 dark:bg-sky-950/40 dark:text-sky-200">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-sky-600 dark:bg-sky-400" />
                Pending
            </span>
        );
    }
    if (type === 'milestone') {
        if (row?.isDisputeRaised) {
            return (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-rose-900 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-200">
                    <span className="h-1.5 w-1.5 rounded-full bg-rose-600 dark:bg-rose-400" />
                    Disputed
                </span>
            );
        }
        if (isClaimed) {
            return (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-teal-900 dark:border-teal-900/50 dark:bg-teal-950/40 dark:text-teal-200">
                    <span className="h-1.5 w-1.5 rounded-full bg-teal-600 dark:bg-teal-500" />
                    Completed
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-violet-900 dark:border-violet-900/50 dark:bg-violet-950/40 dark:text-violet-200">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-violet-600 dark:bg-violet-400" />
                Active
            </span>
        );
    }
    if (isClaimed === false) {
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-600 dark:bg-amber-400" />
                Pending
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-teal-900 dark:border-teal-900/50 dark:bg-teal-950/40 dark:text-teal-200">
            <span className="h-1.5 w-1.5 rounded-full bg-teal-600 dark:bg-teal-500" />
            Settled
        </span>
    );
};

const shortenHash = (hash) => {
    if (!hash || typeof hash !== 'string') return '';
    if (hash.length < 14) return hash;
    return `${hash.slice(0, 8)}…${hash.slice(-6)}`;
};

const CopyBtn = ({ value, className = '' }) => (
    <button
        type="button"
        onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(value);
        }}
        className={`text-zinc-400 transition-colors hover:text-teal-700 dark:text-zinc-500 dark:hover:text-teal-400 ${className}`}
        title="Copy"
    >
        <i className={`${copyIcon} text-[10px]`} />
    </button>
);

const DetailRow = ({ label, children }) => (
    <div className="flex items-start gap-3">
        <span className="mt-1 w-24 flex-shrink-0 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-500">
            {label}
        </span>
        <div className="flex-1 min-w-0 flex items-center gap-2">{children}</div>
    </div>
);

/* ------------------------------------------------------------------ */
/* Skeleton                                                           */
/* ------------------------------------------------------------------ */

const SkeletonRow = () => (
    <div className="flex animate-pulse items-center gap-3 px-4 py-3.5 sm:gap-4 sm:px-5">
        <div className="h-9 w-9 flex-shrink-0 rounded-full bg-zinc-200 dark:bg-zinc-800" />
        <div className="min-w-0 flex-1 space-y-1.5">
            <div className="h-3 w-32 rounded bg-zinc-200 dark:bg-zinc-800" />
            <div className="h-2.5 w-24 rounded bg-zinc-100 dark:bg-zinc-800/80" />
        </div>
        <div className="hidden h-5 w-16 flex-shrink-0 rounded-full bg-zinc-200 dark:bg-zinc-800 sm:block" />
        <div className="h-5 w-16 flex-shrink-0 rounded-full bg-zinc-200 dark:bg-zinc-800" />
        <div className="h-3 w-3 flex-shrink-0 rounded bg-zinc-200 dark:bg-zinc-800" />
    </div>
);

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */

const TransactionHistoryList = ({
    rows = [],
    loading = false,
    mode = 'send',
    counterpartyLabel = 'Counterparty',
    counterpartyKey = 'receiver',
    actionLabel,
    onAction,
    actionTone = 'emerald',
    txHashMap = { init: {}, claim: {} },
    explorerUrl = '',
    defaultType = 'Direct',
    emptyTitle = 'No transactions yet',
    emptyDescription = 'Your activity will appear here once you start moving funds.',
    emptyIcon = 'fa-inbox',
    pageSize = 10,
    pagination = null,
    page: controlledPage,
    onPageChange,
}) => {
    const [expandedId, setExpandedId] = useState(null);
    const [internalPage, setInternalPage] = useState(1);
    const meta = directionMetaMap[mode] || directionMetaMap.send;

    const currentPage = controlledPage ?? internalPage;
    const setPage = onPageChange ?? setInternalPage;
    const isServerPaginated = Boolean(pagination);

    useEffect(() => {
        setPage(1);
        setExpandedId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows.length, pagination?.total, mode]);

    const visibleRows = useMemo(() => {
        if (isServerPaginated) return rows;
        const start = (currentPage - 1) * pageSize;
        return rows.slice(start, start + pageSize);
    }, [rows, currentPage, pageSize, isServerPaginated]);

    const totalItems = pagination?.total ?? rows.length;
    const totalPages =
        pagination?.totalPages ??
        Math.max(1, Math.ceil(rows.length / pageSize) || 1);
    const canPrev = currentPage > 1 && !loading;
    const canNext = currentPage < totalPages && !loading;
    const showPagination = totalItems > pageSize || totalPages > 1;
    const rangeStart =
        totalItems === 0 ? 0 : (currentPage - 1) * (pagination?.limit || pageSize) + 1;
    const rangeEnd = isServerPaginated
        ? Math.min(currentPage * (pagination?.limit || pageSize), totalItems)
        : Math.min(currentPage * pageSize, totalItems);

    const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

    const actionToneClasses =
        'bg-teal-700 text-white hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500';

    if (loading) {
        return (
            <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {Array.from({ length: 4 }).map((_, i) => (
                    <SkeletonRow key={i} />
                ))}
            </div>
        );
    }

    if (!rows || rows.length === 0) {
        return (
            <div className="px-5 py-14 text-center">
                <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full border border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/60">
                    <i className={`fa-solid ${emptyIcon} text-zinc-400 dark:text-zinc-500`} />
                </div>
                <p className="text-sm text-zinc-800 dark:text-zinc-200">{emptyTitle}</p>
                <p className="mx-auto mt-1 max-w-xs text-xs text-zinc-500 dark:text-zinc-500">
                    {emptyDescription}
                </p>
            </div>
        );
    }

    return (
        <div>
            <ul className="max-h-[60vh] divide-y divide-zinc-200 overflow-y-auto dark:divide-zinc-800">
            {visibleRows.map((row) => {
                const id = row.id ?? Number(row.paymentId);
                const counterparty = row[counterpartyKey];
                const isOpen = expandedId === id;
                const isClaimed = row.isClaimed;
                const rowType = row.type || defaultType;
                const rowTypeLower = String(rowType).toLowerCase();
                const isRequestRow = rowTypeLower.includes('request');
                const initTxHash = txHashMap?.init?.[id];
                const claimTxHash = txHashMap?.claim?.[id];
                const hasAction = typeof onAction === 'function';
                const displayId = row.requestId ?? id;

                return (
                    <li
                        key={id}
                        className={`transition-colors ${isOpen ? 'bg-zinc-50 dark:bg-zinc-900/50' : ''}`}
                    >
                        {/* Single compact row (no separate desktop/mobile copies) */}
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={() => toggle(id)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggle(id);
                                }
                            }}
                            className="flex cursor-pointer items-center gap-3 px-4 py-3.5 hover:bg-zinc-50 focus:bg-zinc-50 focus:outline-none sm:gap-4 sm:px-5 dark:hover:bg-zinc-900/30 dark:focus:bg-zinc-900/30"
                        >
                            <Avatar address={counterparty} size={38} />

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                    <TokenChip
                                        name={row.tokenName}
                                        image={row.tokenImage}
                                    />
                                    <AmountDisplay
                                        value={row.parsedNumber ?? row.amount}
                                        className={`text-[14px] font-bold ${meta.amount}`}
                                    />
                                </div>
                                <div className="mt-1 flex items-center gap-1.5 min-w-0">
                                    <span
                                        className="truncate font-mono text-[11px] text-zinc-500 dark:text-zinc-500"
                                        title={counterparty}
                                    >
                                        {shortenWalletAddress(counterparty)}
                                    </span>
                                    {counterparty && <CopyBtn value={counterparty} />}
                                </div>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="hidden sm:inline-flex">
                                    <TypePill type={rowType} mode={mode} />
                                </span>
                                <StatusPill isClaimed={isClaimed} row={row} />
                                {hasAction ? (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onAction(row);
                                        }}
                                        className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold shadow-sm transition-colors ${actionToneClasses}`}
                                    >
                                        <i className="fa-solid fa-hand-holding-dollar text-[10px]" />
                                        {actionLabel || 'Claim'}
                                    </button>
                                ) : (
                                    <i
                                        className={`fa-solid fa-chevron-down ml-1 text-[10px] text-zinc-400 transition-transform duration-[420ms] ease-[cubic-bezier(0.22,1,0.36,1)] dark:text-zinc-500 ${isOpen ? 'rotate-180 text-teal-700 dark:text-teal-400' : ''
                                            }`}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Expanded detail panel */}
                        <ExpandablePanel isOpen={isOpen}>
                            <div className="px-4 sm:px-5 pb-5 pt-1">
                                <div className="grid grid-cols-1 gap-x-6 gap-y-3 rounded-xl border border-zinc-200 bg-zinc-50 p-4 text-xs sm:p-5 md:grid-cols-2 dark:border-zinc-800 dark:bg-zinc-900/40">
                                    <DetailRow label="Direction">
                                        <span
                                            className={`inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide ${meta.color}`}
                                        >
                                            <i className={`fa-solid ${meta.icon} text-[10px]`} />
                                            {meta.label}
                                        </span>
                                    </DetailRow>

                                    <DetailRow label={isRequestRow ? 'Request ID' : 'Payment ID'}>
                                        <span className="font-mono text-[12px] text-teal-800 dark:text-teal-400">
                                            #{displayId}
                                        </span>
                                    </DetailRow>

                                    {isRequestRow && row.description ? (
                                        <DetailRow label="Note">
                                            <span className="text-[12px] font-medium text-zinc-800 dark:text-zinc-200">
                                                {row.description}
                                            </span>
                                        </DetailRow>
                                    ) : null}

                                    {String(rowType).toLowerCase() === 'milestone' && (
                                        <DetailRow label="Milestones">
                                            <span className="text-[12px] font-medium text-zinc-800 dark:text-zinc-200">
                                                {row.claimedMilestonesNum ?? 0} of{' '}
                                                {row.milestonesNum ?? 0} claimed
                                            </span>
                                        </DetailRow>
                                    )}

                                    <DetailRow label={counterpartyLabel}>
                                        <span
                                            className="truncate font-mono text-[12px] text-zinc-800 dark:text-zinc-200"
                                            title={counterparty}
                                        >
                                            {shortenWalletAddress(counterparty)}
                                        </span>
                                        {counterparty && (
                                            <CopyBtn
                                                value={counterparty}
                                                className="rounded-md p-1 hover:bg-zinc-200 dark:hover:bg-zinc-800/60"
                                            />
                                        )}
                                    </DetailRow>

                                    <DetailRow label="Amount">
                                        <AmountDisplay
                                            value={row.parsedNumber ?? row.amount}
                                            className={`text-[13px] font-semibold ${meta.amount}`}
                                            suffix={row.tokenName}
                                        />
                                    </DetailRow>

                                    {/* <DetailRow label="Tx Hash">
                                        {initTxHash ? (
                                            <>
                                                <span
                                                    className="truncate font-mono text-[12px] text-zinc-800 dark:text-zinc-200"
                                                    title={initTxHash}
                                                >
                                                    {shortenHash(initTxHash)}
                                                </span>
                                                <CopyBtn
                                                    value={initTxHash}
                                                    className="rounded-md p-1 hover:bg-zinc-200 dark:hover:bg-zinc-800/60"
                                                />
                                                {explorerUrl && (
                                                    <a
                                                        href={`${explorerUrl}/tx/${initTxHash}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-200 hover:text-teal-800 dark:text-zinc-500 dark:hover:bg-zinc-800/60 dark:hover:text-teal-400"
                                                        title="View on Explorer"
                                                    >
                                                        <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
                                                    </a>
                                                )}
                                            </>
                                        ) : (
                                            <span className="text-[11px] italic text-zinc-500 dark:text-zinc-500">
                                                Loading…
                                            </span>
                                        )}
                                    </DetailRow> */}

                                    {isClaimed && claimTxHash && (
                                        <DetailRow label="Claim Tx">
                                            <span
                                                className="truncate font-mono text-[12px] text-zinc-800 dark:text-zinc-200"
                                                title={claimTxHash}
                                            >
                                                {shortenHash(claimTxHash)}
                                            </span>
                                            <CopyBtn
                                                value={claimTxHash}
                                                className="rounded-md p-1 hover:bg-zinc-200 dark:hover:bg-zinc-800/60"
                                            />
                                            {explorerUrl && (
                                                <a
                                                    href={`${explorerUrl}/tx/${claimTxHash}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-200 hover:text-teal-800 dark:text-zinc-500 dark:hover:bg-zinc-800/60 dark:hover:text-teal-400"
                                                    title="View on Explorer"
                                                >
                                                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
                                                </a>
                                            )}
                                        </DetailRow>
                                    )}
                                </div>
                            </div>
                        </ExpandablePanel>
                    </li>
                );
            })}
            </ul>

            {showPagination ? (
                <div className="flex flex-col gap-3 border-t border-zinc-200 px-4 py-3 dark:border-zinc-800 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">
                        Showing {rangeStart}–{rangeEnd} of {totalItems} transaction
                        {totalItems === 1 ? '' : 's'}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            disabled={!canPrev}
                            onClick={() => setPage(currentPage - 1)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                        >
                            <i className="fa-solid fa-chevron-left text-[10px]" aria-hidden />
                            Previous
                        </button>
                        <span className="min-w-[4.5rem] text-center text-xs font-medium tabular-nums text-zinc-600 dark:text-zinc-400">
                            {currentPage} / {totalPages}
                        </span>
                        <button
                            type="button"
                            disabled={!canNext}
                            onClick={() => setPage(currentPage + 1)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                        >
                            Next
                            <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden />
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default TransactionHistoryList;
